/**
 * MCP Protocol Client — JSON-RPC 2.0 over Streamable HTTP.
 *
 * Uses axios directly (not the shared apiClient) because the MCP gateway
 * lives at a different base URL than the main API.
 */

import axios from 'axios';
import type {
  JsonRpcRequest,
  JsonRpcResponse,
  McpTool,
  McpToolCallResult,
  McpResource,
  McpResourceTemplate,
  McpPrompt,
  McpPromptMessage,
} from './types';

// ---------------------------------------------------------------------------
// Types returned by lifecycle RPCs
// ---------------------------------------------------------------------------

export interface McpServerInfo {
  name: string;
  version?: string;
}

export interface McpCapabilities {
  tools?: Record<string, unknown>;
  resources?: Record<string, unknown>;
  prompts?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface McpInitializeResult {
  protocolVersion: string;
  serverInfo: McpServerInfo;
  capabilities: McpCapabilities;
}

export interface McpCompleteResult {
  values: string[];
  total?: number;
  hasMore?: boolean;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class McpProtocolClient {
  readonly endpoint: string;
  private requestId = 0;
  private authHeaders: Record<string, string> = {};

  /**
   * @param baseUrl  - Base URL of the MCP gateway (e.g. `http://localhost:8085`).
   * @param orgSlug  - Optional org slug for vserver-scoped endpoints.
   * @param vserverSlug - Optional vserver slug (requires orgSlug).
   *
   * If both orgSlug and vserverSlug are provided the endpoint becomes
   * `{baseUrl}/{orgSlug}/{vserverSlug}/mcp`, otherwise `{baseUrl}/mcp`.
   */
  constructor(baseUrl: string, orgSlug?: string, vserverSlug?: string) {
    const base = baseUrl.replace(/\/+$/, '');
    const slugPath = [orgSlug, vserverSlug]
      .filter((p): p is string => !!p && p.length > 0)
      .join('/')
      .replace(/\/+/g, '/');
    this.endpoint = slugPath ? `${base}/${slugPath}/mcp` : `${base}/mcp`;
  }

  /** Replace auth headers (e.g. after token refresh). */
  setAuthHeaders(headers: Record<string, string>): void {
    this.authHeaders = headers;
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private nextId(): number {
    this.requestId += 1;
    return this.requestId;
  }

  /**
   * Send a JSON-RPC 2.0 request and return the typed result.
   * Handles both plain JSON and SSE (text/event-stream) responses.
   */
  private async rpc<T>(method: string, params?: Record<string, unknown>): Promise<T> {
    const body: JsonRpcRequest = {
      jsonrpc: '2.0',
      id: this.nextId(),
      method,
      ...(params !== undefined && { params }),
    };

    const response = await axios.post(this.endpoint, body, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/event-stream',
        ...this.authHeaders,
      },
      timeout: 30_000,
      // Accept any HTTP status so we can inspect JSON-RPC errors ourselves.
      validateStatus: () => true,
    });

    const contentType: string = String(response.headers['content-type'] ?? '');

    let rpcResponse: JsonRpcResponse<T>;

    if (contentType.includes('text/event-stream')) {
      rpcResponse = this.parseSSE<T>(
        typeof response.data === 'string' ? response.data : String(response.data),
      );
    } else {
      rpcResponse = response.data as JsonRpcResponse<T>;
    }

    if (rpcResponse.error) {
      const err = new Error(rpcResponse.error.message);
      (err as unknown as Record<string, unknown>).code = rpcResponse.error.code;
      (err as unknown as Record<string, unknown>).data = rpcResponse.error.data;
      throw err;
    }

    return rpcResponse.result as T;
  }

  /**
   * Parse an SSE payload and extract the last JSON-RPC response.
   *
   * The MCP Streamable HTTP transport may return `text/event-stream` where
   * each event carries a `data:` line containing a full JSON-RPC response.
   * We want the *last* one (the final result).
   */
  private parseSSE<T>(text: string): JsonRpcResponse<T> {
    const lines = text.split('\n');
    let lastData: string | undefined;

    for (const line of lines) {
      if (line.startsWith('data:')) {
        lastData = line.slice(5).trim();
      }
    }

    if (!lastData) {
      throw new Error('No data lines found in SSE response');
    }

    return JSON.parse(lastData) as JsonRpcResponse<T>;
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  async initialize(): Promise<McpInitializeResult> {
    return this.rpc<McpInitializeResult>('initialize', {
      protocolVersion: '2024-11-05',
      capabilities: {},
      clientInfo: { name: 'asab-frontend', version: '1.0.0' },
    });
  }

  async ping(): Promise<void> {
    await this.rpc<Record<string, never>>('ping');
  }

  // -------------------------------------------------------------------------
  // Tools
  // -------------------------------------------------------------------------

  async listTools(): Promise<McpTool[]> {
    const result = await this.rpc<{ tools: McpTool[] }>('tools/list');
    return result.tools;
  }

  async callTool(name: string, args?: Record<string, unknown>): Promise<McpToolCallResult> {
    return this.rpc<McpToolCallResult>('tools/call', { name, arguments: args ?? {} });
  }

  // -------------------------------------------------------------------------
  // Resources
  // -------------------------------------------------------------------------

  async listResources(): Promise<McpResource[]> {
    const result = await this.rpc<{ resources: McpResource[] }>('resources/list');
    return result.resources;
  }

  async listResourceTemplates(): Promise<McpResourceTemplate[]> {
    const result = await this.rpc<{ resourceTemplates: McpResourceTemplate[] }>(
      'resources/templates/list',
    );
    return result.resourceTemplates;
  }

  async readResource(
    uri: string,
  ): Promise<{ contents: Array<{ uri: string; text?: string; blob?: string; mimeType?: string }> }> {
    return this.rpc('resources/read', { uri });
  }

  // -------------------------------------------------------------------------
  // Prompts
  // -------------------------------------------------------------------------

  async listPrompts(): Promise<McpPrompt[]> {
    const result = await this.rpc<{ prompts: McpPrompt[] }>('prompts/list');
    return result.prompts;
  }

  async getPrompt(
    name: string,
    args?: Record<string, string>,
  ): Promise<{ messages: McpPromptMessage[] }> {
    return this.rpc('prompts/get', { name, arguments: args ?? {} });
  }

  // -------------------------------------------------------------------------
  // Completions
  // -------------------------------------------------------------------------

  async complete(
    ref: { type: string; name: string },
    argument: { name: string; value: string },
  ): Promise<McpCompleteResult> {
    const result = await this.rpc<{ completion: McpCompleteResult }>('completion/complete', {
      ref,
      argument,
    });
    return result.completion;
  }
}
